import { useState, useEffect, useMemo } from 'react';
import { csAdminApi } from '../services/api';
import { LOOKUP_FALLBACK } from '../constants/lookupFallback';

const cache = new Map();
const invalidationListeners = new Set();

function cacheKey(category, activeOnly) {
  return `${category || '__all__'}:${activeOnly ? '1' : '0'}`;
}

function notifyLookupInvalidation() {
  invalidationListeners.forEach((listener) => {
    try {
      listener();
    } catch (_) {
      // ignore listener errors
    }
  });
}

export function subscribeLookupInvalidation(listener) {
  invalidationListeners.add(listener);
  return () => invalidationListeners.delete(listener);
}

export async function fetchLookups(category, { activeOnly = true, bypassCache = false } = {}) {
  const key = cacheKey(category, activeOnly);
  if (!bypassCache && cache.has(key)) return cache.get(key);

  const res = await csAdminApi.getLookups({
    category,
    activeOnly: activeOnly ? 1 : 0,
  });
  const data = {
    data: res.data?.data || [],
    tablesMissing: !!res.data?.tablesMissing,
    categories: res.data?.categories || [],
  };
  cache.set(key, data);
  return data;
}

export function clearLookupCache() {
  cache.clear();
  notifyLookupInvalidation();
}

export function useLookups(category, { activeOnly = true } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tablesMissing, setTablesMissing] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => subscribeLookupInvalidation(() => {
    setRefreshToken((token) => token + 1);
  }), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const bypassCache = refreshToken > 0;
    fetchLookups(category, { activeOnly, bypassCache })
      .then((result) => {
        if (cancelled) return;
        setItems(result.data || []);
        setTablesMissing(!!result.tablesMissing);
      })
      .catch(() => {
        if (!cancelled) {
          let fallback = LOOKUP_FALLBACK;
          if (category) fallback = fallback.filter((i) => i.category === category);
          setItems(fallback);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [category, activeOnly, refreshToken]);

  const byCode = useMemo(() => {
    const map = new Map();
    items.forEach((item) => map.set(item.code, item));
    return map;
  }, [items]);

  return { items, byCode, loading, tablesMissing };
}

export function normalizeLookupMetadata(metadata) {
  if (!metadata) return {};
  if (typeof metadata === 'object') return metadata;
  try {
    return JSON.parse(String(metadata));
  } catch (_) {
    return {};
  }
}

export function getCompanyTypeStructure(lookup) {
  const meta = normalizeLookupMetadata(lookup?.metadata);
  return meta.structure || null;
}

export function filterCompanyTypesByStructure(companyTypes, structure) {
  return companyTypes.filter((type) => getCompanyTypeStructure(type) === structure);
}

export function isUaeCountry(countryCode, countryLookups) {
  if (!countryCode) return false;
  const match = countryLookups?.find((c) => c.code === countryCode);
  if (match?.metadata?.is_uae) return true;
  const normalized = String(countryCode).toUpperCase();
  return normalized === 'UAE' || normalized === 'ARE' || normalized.includes('UNITED ARAB EMIRATES');
}

export function suggestGovernanceType(countryCode, countryLookups, companyTypeLookups) {
  const uae = isUaeCountry(countryCode, countryLookups);
  const subsidiaryTypes = filterCompanyTypesByStructure(companyTypeLookups, 'SUBSIDIARY');
  if (!subsidiaryTypes.length) return 'LS';
  if (uae) {
    const ls = subsidiaryTypes.find((t) => t.code === 'LS');
    return ls?.code || subsidiaryTypes[0].code;
  }
  const fs = subsidiaryTypes.find((t) => t.code === 'FS');
  return fs?.code || subsidiaryTypes[0].code;
}
